import { useMemo, useState, useEffect, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useTranslation, type LangCode } from "../../i18n";
import { useTheme } from "../../lib/theme";
import { useAuthStore } from "../../state/auth";
import { getLocationMeta, type KioskLocationMeta } from "../../data/kioskLocations";
import { getHospitalImage, getAvailableFloors } from "../../data/hospitalImages";
import type { RecentScan } from "../../services/scans";
import { useHospitalColors } from "../../utils/hospitalColors";
import { CheckpointHistory, type CheckpointSummary } from "../CheckpointHistory";
import { Map as MapIcon, Layers, Activity, ChevronLeft, ChevronRight, Lock, Unlock, ZoomIn, ZoomOut, Save, BarChart3, XCircle } from "lucide-react";
import Plotly from 'plotly.js-basic-dist';
import createPlotlyComponent from 'react-plotly.js/factory';

const Plot = createPlotlyComponent(Plotly);

interface HospitalMapProps {
  scans: RecentScan[];
  hospitalId?: string;
  period?: 'today' | 'week' | 'month' | 'quarter';
  /**
   * Display label for the active time range (e.g. "Last 30 days"). When provided
   * it overrides the coarse `period` labels so the map's counts/tooltips/chart
   * title reflect whatever range the page-level filter has selected.
   */
  rangeLabel?: string;
}

// DB checkpoint floors are sometimes stored with a trailing "F" (e.g. "1F", "10F")
// while the floor-plan selector uses bare values ("1", "10"). Normalize both sides
// of any floor comparison so a checkpoint isn't hidden purely over that formatting
// difference. Non-numeric floors ("Ground", "SB", "LL") pass through unchanged.
function normalizeFloor(value: string | number | null | undefined): string {
  return String(value ?? "").trim().toUpperCase().replace(/^(\d+)F$/, "$1");
}

// Only DB-backed checkpoints (uuid ids) can be drilled into via the history
// endpoint; scan-only/legacy ids (codes) are not openable.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function HospitalMap({ scans, hospitalId, period = 'today', rangeLabel }: HospitalMapProps) {
  const { t, lang } = useTranslation();
  // Resolved label shown wherever the active timeframe is surfaced.
  const periodDisplayLabel = rangeLabel ?? ({
    today: t("home.mapTodaysScans", "Today's Scans"),
    week: t("home.mapLast7Days", "Last 7 Days"),
    month: t("home.mapLast30Days", "Last 30 Days"),
    quarter: t("home.mapLastQuarter", "Last Quarter")
  } as const)[period];
  const { user } = useAuthStore();
  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isSupervisor = user?.role === "SUPERVISOR";
  const canManageMarkers = isAdmin || isSupervisor;
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPlacementMode, setIsPlacementMode] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingPosition, setPendingPosition] = useState<{ x: number; y: number } | null>(null);
  const [newLocationForm, setNewLocationForm] = useState({ name: "", qr_payload: "", building: "", floor: "", existingId: "" });
  const [dbLocations, setDbLocations] = useState<Record<string, { map_x: number; map_y: number; name?: string; floor?: string; building?: string; hospital_id?: string }>>({});
  const [allDbCheckpoints, setAllDbCheckpoints] = useState<Array<{ id: string; name: string; qr_payload: string; floor: string; building: string; section?: string }>>([]);
  const [selectedFloor, setSelectedFloor] = useState<string>("1");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Checkpoint whose scan-history drill-down panel is open (null = closed).
  const [historyCheckpoint, setHistoryCheckpoint] = useState<CheckpointSummary | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Fetch true locations from DB
  useEffect(() => {
    const url = hospitalId ? `/api/locations?hospital_id=${hospitalId}` : "/api/locations";
    const headers: Record<string, string> = {};
    if (user?.email) headers["x-user-email"] = user.email;
    const token = localStorage.getItem("authToken");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(url, { headers })
      .then((res) => res.json())
      .then((data) => {
        if (data.locations) {
          const map = {} as Record<string, { map_x: number; map_y: number; name?: string; floor?: string; building?: string; hospital_id?: string }>;
          const checkpoints: Array<{ id: string; name: string; qr_payload: string; floor: string; building: string; section?: string }> = [];
          data.locations.forEach((l: any) => {
            checkpoints.push({
              id: l.id,
              name: l.name || l.id,
              qr_payload: l.qr_payload || '',
              floor: l.floor || '1',
              building: l.building || '',
              section: l.section
            });
            if (l.map_x != null && l.map_y != null) {
              map[l.id] = {
                map_x: Number(l.map_x),
                map_y: Number(l.map_y),
                name: l.name,
                floor: l.floor,
                building: l.building,
                hospital_id: l.hospital_id
              };
            }
          });
          setDbLocations(map);
          setAllDbCheckpoints(checkpoints);
        }
      })
      .catch((err) => console.error("Failed to fetch locations", err));
  }, [hospitalId]);

  const handleDragStart = (e: React.MouseEvent | React.PointerEvent, locationId: string) => {
    if (!isEditMode) return;
    e.stopPropagation();
    // e.nativeEvent.stopImmediatePropagation(); // More aggressive stop
    e.preventDefault();
    setDraggingId(locationId);
  };

  // Robust Drag Handling
  useEffect(() => {
    if (!draggingId) return;

    const handleWindowPointerMove = (e: PointerEvent) => {
      if (!mapContainerRef.current) return;
      const rect = mapContainerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      setDbLocations(prev => ({
        ...prev,
        [draggingId]: { ...prev[draggingId], map_x: clampedX, map_y: clampedY }
      }));
    };

    const handleWindowPointerUp = async () => {
      if (!draggingId) return;

      const loc = dbLocations[draggingId];
      if (loc) {
        try {
          const saveHeaders: Record<string, string> = { "Content-Type": "application/json" };
          if (user?.email) saveHeaders["x-user-email"] = user.email;
          const saveToken = localStorage.getItem("authToken");
          if (saveToken) saveHeaders["Authorization"] = `Bearer ${saveToken}`;

          await fetch("/api/locations?action=update-position", {
            method: "POST",
            headers: saveHeaders,
            body: JSON.stringify({ id: draggingId, x: loc.map_x, y: loc.map_y })
          });
        } catch (err) {
          console.error("Failed to save position", err);
        }
      }
      setDraggingId(null);
    };

    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleWindowPointerUp);

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerUp);
    };
  }, [draggingId, dbLocations]);

  const handleMapClick = (e: React.MouseEvent) => {
    if (!isPlacementMode || !mapContainerRef.current) return;
    const rect = mapContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingPosition({ x, y });
    setNewLocationForm({ ...newLocationForm, floor: selectedFloor });
    setShowCreateModal(true);
  };

  const handleCreateLocation = async () => {
    if (!hospitalId || !pendingPosition) return;

    try {
      const createHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (user?.email) createHeaders["x-user-email"] = user.email;
      const createToken = localStorage.getItem("authToken");
      if (createToken) createHeaders["Authorization"] = `Bearer ${createToken}`;

      let res: Response;

      if (newLocationForm.existingId) {
        // Existing checkpoint — update its map position AND move it onto the floor
        // the admin is currently viewing, so it lands where they clicked instead of
        // staying on its originally-seeded floor. The checkpoint keeps its id and
        // qr_payload, so scan-count linkage is preserved.
        res = await fetch("/api/locations?action=update-position", {
          method: "POST",
          headers: createHeaders,
          body: JSON.stringify({
            id: newLocationForm.existingId,
            x: pendingPosition.x,
            y: pendingPosition.y,
            floor: selectedFloor
          })
        });
      } else {
        // Brand-new custom checkpoint
        res = await fetch("/api/locations", {
          method: "POST",
          headers: createHeaders,
          body: JSON.stringify({
            hospital_id: hospitalId,
            name: newLocationForm.name,
            qr_payload: newLocationForm.qr_payload,
            map_x: pendingPosition.x,
            map_y: pendingPosition.y,
            floor: newLocationForm.floor,
            building: newLocationForm.building
          })
        });
      }

      if (res.ok) {
        const data = await res.json();
        const newLoc = data.location;
        setDbLocations(prev => ({
          ...prev,
          [newLoc.id]: {
            map_x: Number(newLoc.map_x),
            map_y: Number(newLoc.map_y),
            name: newLoc.name,
            floor: newLoc.floor,
            building: newLoc.building,
            hospital_id: newLoc.hospital_id
          }
        }));
        // Jump to the floor the marker actually lives on so it's immediately
        // visible — e.g. placing an existing "10F" checkpoint switches the view
        // to floor "10" instead of leaving it hidden on the current floor.
        const targetFloor = availableFloors.find(
          (f) => normalizeFloor(f) === normalizeFloor(newLoc.floor)
        );
        if (targetFloor && targetFloor !== selectedFloor) {
          setSelectedFloor(targetFloor);
        }
        setShowCreateModal(false);
        setIsPlacementMode(false);
        setPendingPosition(null);
        setNewLocationForm({ name: "", qr_payload: "", building: "", floor: "", existingId: "" });
      } else {
        const error = await res.json();
        alert(error.error || t("home.failedSaveLocation", "Failed to save location"));
      }
    } catch (err) {
      console.error("Failed to save location", err);
      alert(t("home.internalServerError", "Internal Server Error"));
    }
  };

  const handleDeleteLocation = async (id: string) => {
    if (!canManageMarkers || !id) return;

    if (!confirm(t("home.confirmDeleteCheckpoint", "Are you sure you want to delete this checkpoint? This action cannot be undone."))) {
      return;
    }

    try {
      const deleteHeaders: Record<string, string> = {};
      if (user?.email) deleteHeaders["x-user-email"] = user.email;
      const deleteToken = localStorage.getItem("authToken");
      if (deleteToken) deleteHeaders["Authorization"] = `Bearer ${deleteToken}`;

      const res = await fetch(`/api/locations?id=${id}`, {
        method: "DELETE",
        headers: deleteHeaders
      });

      if (res.ok) {
        setDbLocations(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else {
        const error = await res.json();
        alert(error.error || t("home.failedDeleteLocation", "Failed to delete location"));
      }
    } catch (err) {
      console.error("Failed to delete location", err);
      alert(t("home.internalServerError", "Internal Server Error"));
    }
  };

  useEffect(() => {
    // Cleanup legacy event if needed
    return () => { };
  }, []);



  const availableFloors = useMemo(() => {
    return hospitalId ? getAvailableFloors(hospitalId) : [];
  }, [hospitalId]);

  // Reset floor when hospital changes
  useEffect(() => {
    if (availableFloors.length > 0) {
      // Try to find "1" or "Ground", otherwise use the first one
      const defaultFloor = availableFloors.includes("1") ? "1" : (availableFloors.includes("Ground") ? "Ground" : availableFloors[0]);

      // If selectedFloor is not in available floors, reset to default
      if (!availableFloors.includes(selectedFloor)) {
        setSelectedFloor(defaultFloor);
      }
    } else {
      setSelectedFloor("1");
    }
  }, [hospitalId, availableFloors]);

  const campusMapImage = getHospitalImage(hospitalId, selectedFloor);

  const markers = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const fifteenMinsAgo = new Date(now.getTime() - 15 * 60000);
    const oneHourAgo = new Date(now.getTime() - 60 * 60000);

    const data = new Map<string, {
      latest: RecentScan | null;
      countToday: number;
      recentCount: number;
      isActive: boolean;
    }>();

    for (const scan of scans) {
      const entry = data.get(scan.location_id) ?? {
        latest: null,
        countToday: 0,
        recentCount: 0,
        isActive: false
      };

      const scanDate = new Date(scan.timestamp);
      const scanTime = scanDate.getTime();

      if (!entry.latest || scanTime > new Date(entry.latest.timestamp).getTime()) {
        entry.latest = scan;
      }

      // Count for the entire data set (which is already filtered by start_date in the service)
      entry.countToday += 1;

      if (scanDate >= oneHourAgo) {
        entry.recentCount += 1;
      }

      if (scanDate >= fifteenMinsAgo) {
        entry.isActive = true;
      }

      data.set(scan.location_id, entry);
    }

    // Combine scans with all DB locations to ensure every registered point is shown
    const allLocationIds = new Set([...Array.from(data.keys()), ...Object.keys(dbLocations)]);

    return Array.from(allLocationIds)
      .map((locationId) => {
        const info = data.get(locationId) ?? {
          latest: null,
          countToday: 0,
          recentCount: 0,
          isActive: false
        };
        const meta = getLocationMeta(locationId);
        // Override with DB location if available
        const dbLoc = dbLocations[locationId];
        const effectiveLocation = dbLoc
          ? {
            ...meta,
            id: locationId,
            hospital_id: dbLoc.hospital_id,
            name: dbLoc.name || meta.name || locationId,
            floor: dbLoc.floor || meta.floor,
            building: dbLoc.building || meta.building,
            mapPosition: { x: dbLoc.map_x, y: dbLoc.map_y }
          }
          : { ...meta, id: locationId };

        return {
          location: effectiveLocation,
          scan: info.latest,
          countToday: info.countToday,
          recentCount: info.recentCount,
          isActive: info.isActive
        };
      })
      .filter(({ location }) => {
        // Filter by hospital first
        if (hospitalId && (location as any).hospital_id && (location as any).hospital_id !== hospitalId) {
          return false;
        }
        // Then by floor — normalized so DB floors like "10F" match selector "10".
        if (availableFloors.length > 0) {
          return normalizeFloor(location.floor) === normalizeFloor(selectedFloor);
        }
        return true;
      });
  }, [scans, selectedFloor, availableFloors, dbLocations, period]);

  const handleNextFloor = () => {
    const currentIndex = availableFloors.indexOf(selectedFloor);
    if (currentIndex < availableFloors.length - 1) {
      setSelectedFloor(availableFloors[currentIndex + 1]);
    }
  };

  const handlePrevFloor = () => {
    const currentIndex = availableFloors.indexOf(selectedFloor);
    if (currentIndex > 0) {
      setSelectedFloor(availableFloors[currentIndex - 1]);
    }
  };

  const statusColor = (status: RecentScan["status"]) => {
    if (!hospitalId) return "bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]";
    switch (status) {
      case "OK":
        return "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]";
      case "INVALID":
        return "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]";
      default:
        return "bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]";
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MapIcon className="h-5 w-5 text-blue-600" />
            {t("home.mapTitle", "Hospital Live Activity Map")}
            {canManageMarkers && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => {
                    setIsEditMode(!isEditMode);
                    if (isPlacementMode) setIsPlacementMode(false);
                  }}
                  className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${isEditMode ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                >
                  {isEditMode ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {isEditMode ? t("home.editModeOn", "Edit Mode ON") : t("home.editPositions", "Edit Positions")}
                </button>
                <button
                  onClick={() => {
                    setIsPlacementMode(!isPlacementMode);
                    if (isEditMode) setIsEditMode(false);
                  }}
                  className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${isPlacementMode ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                >
                  <MapIcon className="w-3 h-3" />
                  {isPlacementMode ? t("home.cancelPlacement", "Cancel Placement") : t("home.addNewMarker", "Add New Marker")}
                </button>
              </div>
            )}
          </h3>
          <p className="text-sm text-gray-600">
            {isEditMode ? t("home.dragToReposition", "Drag markers to reposition. Changes save automatically.") : (isPlacementMode ? t("home.clickToPlace", "Click anywhere on the map to place a new marker.") : t("home.mapSubtitle", "Real-time patrol tracking and checkpoint status"))}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {availableFloors.length > 0 && (
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg border border-gray-200">
              <button
                onClick={handlePrevFloor}
                disabled={availableFloors.indexOf(selectedFloor) === 0}
                className="p-1 rounded-md hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              </button>
              <div className="px-3 py-1 text-xs font-bold text-blue-600 bg-white shadow-sm rounded-md min-w-[80px] text-center">
                {t("home.floorN", "Floor {n}").replace("{n}", selectedFloor)}
              </div>
              <button
                onClick={handleNextFloor}
                disabled={availableFloors.indexOf(selectedFloor) === availableFloors.length - 1}
                className="p-1 rounded-md hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              >
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          )}
          <div className="hidden sm:flex items-center gap-3 text-xs text-gray-600">
            <LegendDot className="bg-emerald-500" label={t("home.legendOk", "OK")} />
            <LegendDot className="bg-amber-500" label={t("home.legendInvalid", "Invalid")} />
            <LegendDot className="bg-rose-500" label={t("home.legendError", "Error")} />
          </div>
        </div>
      </header>

      {!hospitalId ? (
        <HospitalComparisonChart scans={scans} periodLabel={periodDisplayLabel} />
      ) : (
        <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-white group/map">
          <TransformWrapper
            initialScale={1}
            minScale={1}
            maxScale={4}
            disabled={isEditMode || !!draggingId} // Disable zoom when editing/dragging
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {/* Zoom Controls */}
                <div className="absolute top-4 right-4 z-[100] flex flex-col gap-2 bg-white/10 backdrop-blur-md p-1.5 rounded-lg border border-white/20 shadow-xl">
                  <button onClick={() => zoomIn()} className="p-1.5 bg-white/90 hover:bg-white text-gray-800 rounded shadow-sm hover:scale-110 transition-all">
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button onClick={() => zoomOut()} className="p-1.5 bg-white/90 hover:bg-white text-gray-800 rounded shadow-sm hover:scale-110 transition-all">
                    <ZoomOut className="w-4 h-4" />
                  </button>
                </div>

                <TransformComponent wrapperClass="!w-full !h-[600px]" contentClass="!w-full !h-full">
                  <div
                    ref={mapContainerRef}
                    onClick={handleMapClick}
                    className={`relative w-full h-[600px] ${isPlacementMode ? 'cursor-cell ring-2 ring-emerald-500 ring-inset shadow-[inset_0_0_20px_rgba(16,185,129,0.3)]' : 'cursor-crosshair'}`}
                    style={{
                      backgroundImage: `url(${campusMapImage})`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center"
                    }}
                  >


                    {markers.map(({ location, scan, countToday, recentCount, isActive }) => (
                      <MapMarker
                        key={location.id}
                        location={location}
                        statusClass={statusColor(scan?.status ?? "OK")}
                        scan={scan}
                        countToday={countToday}
                        recentCount={recentCount}
                        isActive={isActive}
                        isEditMode={isEditMode}
                        periodLabel={periodDisplayLabel}
                        onDragStart={(e) => handleDragStart(e, location.id)}
                        onDelete={() => handleDeleteLocation(location.id)}
                        onSelect={() => {
                          // Drill into scan history — but only outside the edit/
                          // placement workflows, and only for DB-backed (uuid)
                          // checkpoints the history endpoint can resolve.
                          if (isEditMode || isPlacementMode) return;
                          if (!UUID_RE.test(location.id)) return;
                          setHistoryCheckpoint({
                            id: location.id,
                            name: location.name,
                            building: location.building,
                            floor: location.floor != null ? String(location.floor) : null,
                          });
                        }}
                        t={t}
                        lang={lang}
                      />
                    ))}


                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>

          {/* Live Indicator */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-md border border-white/10 z-[50] pointer-events-none">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">{t("home.liveSystem", "Live System")}</span>
          </div>

          {/* Creation Modal */}
          {showCreateModal && pendingPosition && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="bg-slate-900 px-6 py-4">
                  <h4 className="text-lg font-bold text-white">{t("home.placeCheckpoint", "Place Checkpoint")}</h4>
                  <p className="text-xs text-slate-300 mt-1">{t("home.positionLabel", "Position:")} {pendingPosition.x.toFixed(1)}%, {pendingPosition.y.toFixed(1)}% — {t("home.floorN", "Floor {n}").replace("{n}", selectedFloor)}</p>
                </div>
                <div className="p-6 space-y-4">
                  {/* Dropdown to pick from existing seeded checkpoints */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t("home.selectCheckpoint", "Select Checkpoint")}</label>
                    {allDbCheckpoints.length > 0 ? (
                      <select
                        autoFocus
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900 text-sm"
                        value={newLocationForm.name ? `${newLocationForm.existingId}|||${newLocationForm.name}|||${newLocationForm.qr_payload}|||${newLocationForm.building}` : ""}
                        onChange={e => {
                          if (e.target.value === "__custom__") {
                            setNewLocationForm({ name: "", qr_payload: "", building: "", floor: selectedFloor, existingId: "" });
                          } else if (e.target.value) {
                            const [id, name, qr, building] = e.target.value.split("|||");
                            setNewLocationForm({ name, qr_payload: qr, building, floor: selectedFloor, existingId: id });
                          } else {
                            setNewLocationForm({ name: "", qr_payload: "", building: "", floor: selectedFloor, existingId: "" });
                          }
                        }}
                      >
                        <option value="">{t("home.chooseCheckpoint", "— Choose a checkpoint —")}</option>
                        {allDbCheckpoints
                          .filter(cp => cp.floor === selectedFloor)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(cp => (
                            <option key={cp.id} value={`${cp.id}|||${cp.name}|||${cp.qr_payload}|||${cp.building || ''}`}>
                              {cp.name}{cp.section ? ` (${cp.section})` : ''}
                            </option>
                          ))}
                        <option disabled>────────────</option>
                        {allDbCheckpoints
                          .filter(cp => cp.floor !== selectedFloor)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(cp => (
                            <option key={cp.id} value={`${cp.id}|||${cp.name}|||${cp.qr_payload}|||${cp.building || ''}`}>
                              {cp.name} ({t("home.floorN", "Floor {n}").replace("{n}", cp.floor)})
                            </option>
                          ))}
                        <option disabled>────────────</option>
                        <option value="__custom__">{t("home.createCustomCheckpoint", "+ Create custom checkpoint...")}</option>
                      </select>
                    ) : (
                      <div className="w-full px-3 py-2.5 border border-amber-200 rounded-lg bg-amber-50 text-amber-700 text-sm">
                        {t("home.noCheckpointsLoaded", "No checkpoints loaded. Check database connection or add one manually below.")}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t("home.checkpointName", "Checkpoint Name")}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder={t("home.checkpointNamePlaceholder", "e.g. Finance (West)")}
                      value={newLocationForm.name}
                      onChange={e => setNewLocationForm({ ...newLocationForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t("home.qrPayload", "QR Payload")}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder={t("home.qrPayloadPlaceholder", "e.g. FINANCE_WEST")}
                      value={newLocationForm.qr_payload}
                      onChange={e => setNewLocationForm({ ...newLocationForm, qr_payload: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t("home.building", "Building")}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={newLocationForm.building}
                        onChange={e => setNewLocationForm({ ...newLocationForm, building: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t("home.floor", "Floor")}</label>
                      <input
                        readOnly
                        type="text"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700"
                        value={newLocationForm.floor}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex border-t border-gray-100">
                  <button
                    onClick={() => { setShowCreateModal(false); setIsPlacementMode(false); }}
                    className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    onClick={handleCreateLocation}
                    disabled={!newLocationForm.name || !newLocationForm.qr_payload}
                    className="flex-1 px-4 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {t("home.saveCheckpoint", "Save Checkpoint")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {historyCheckpoint && (
        <CheckpointHistory
          checkpoint={historyCheckpoint}
          onClose={() => setHistoryCheckpoint(null)}
        />
      )}
    </div >
  );
}

function HospitalComparisonChart({ scans, periodLabel }: { scans: RecentScan[]; periodLabel: string }) {
  const { t } = useTranslation();
  const [themeMode] = useTheme();
  const isDark = themeMode === "dark";
  // Shared canonical hospital→color map (utils/hospitalColors). Coloring each bar
  // by its hospital NAME — not by bar position — makes a hospital's color match
  // the Scan Map legend and everywhere else, no matter the sort order here.
  const hospitalColors = useHospitalColors();
  // Plotly renders inline SVG whose text is NOT reached by the global dark
  // stylesheet, so its colors must be set explicitly per theme or the labels
  // wash out (dim gray on the dark card).
  const textColor = isDark ? "#F3F4F6" : "#1F2937";
  const gridColor = isDark ? "rgba(255,255,255,0.14)" : "#E5E7EB";
  const hospitalCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    scans.forEach(scan => {
      const name = scan.hospital_name || t("home.unknownHospital", "Unknown Hospital");
      counts[name] = (counts[name] || 0) + 1;
    });
    // Sort by count descending
    return Object.entries(counts)
      .sort((a, b) => a[1] - b[1]); // Ascending for horizontal bar chart (bottom to top)
  }, [scans]);

  const xData = hospitalCounts.map(item => item[1]);
  const yData = hospitalCounts.map(item => item[0]);

  return (
    <div className="w-full h-[600px] flex items-center justify-center bg-gray-50/50 rounded-lg border border-gray-100 p-6 dark:bg-[#242424] dark:border-[#3A3A3A]">
      {/* Kept on the neutral gray dark surface (not the app navy) — the brand bar
          colors read better against it. Explicit dark: overrides beat the global
          bg-white→navy remap, same as the Distribution plot. */}
      <div className="w-full h-full max-w-4xl bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col dark:bg-[#2D2D2D] dark:border-[#404040]">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2 dark:bg-[#242424] dark:border-[#3A3A3A]">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          <h4 className="font-semibold text-gray-800">{t("home.hospitalPerformanceComparison", "Hospital Performance Comparison")} ({periodLabel})</h4>
        </div>
        <div className="flex-1 w-full p-2">
          <Plot
            data={[
              {
                type: 'bar',
                x: xData,
                y: yData,
                orientation: 'h',
                marker: {
                  color: yData.map((name) => hospitalColors.colorFor({ name })),
                  line: {
                    color: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.6)',
                    width: 1
                  }
                },
                // Value labels at the end of each bar, colored per theme so the
                // count is always legible.
                text: xData.map((v) => String(v)),
                textposition: 'outside',
                cliponaxis: false,
                textfont: {
                  size: 13,
                  color: textColor,
                  family: 'Inter, system-ui, sans-serif'
                },
                hoverinfo: 'x+y',
                hovertemplate: `<b>%{y}</b><br>${t("home.chartScans", "Scans:")} %{x}<extra></extra>`
              }
            ]}
            layout={{
              autosize: true,
              margin: { l: 200, r: 60, t: 30, b: 50 },
              xaxis: {
                title: { text: t("home.numberOfScans", "Number of Scans"), font: { color: textColor } },
                gridcolor: gridColor,
                zerolinecolor: gridColor,
                tickfont: { color: textColor, family: 'Inter, system-ui, sans-serif' }
              },
              yaxis: {
                title: { text: '' },
                automargin: true,
                tickfont: {
                  size: 13,
                  color: textColor,
                  family: 'Inter, system-ui, sans-serif'
                }
              },
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              showlegend: false,
              font: {
                family: 'Inter, system-ui, sans-serif',
                color: textColor
              }
            }}
            config={{
              responsive: true,
              displayModeBar: false
            }}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        <div className="p-3 border-t border-gray-100 bg-gray-50/50 text-center">
          <p className="text-xs text-gray-500 italic">{t("home.comparingScanVolume", "Comparing active scan volume across all registered sites")}</p>
          <p className="text-[11px] text-gray-400 mt-1">
            {t(
              "home.comparisonVsMapNote",
              "Counts include every recorded scan. The Scan Map plots only scans with GPS coordinates, so its total may be lower."
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-2.5 rounded-full shadow-sm ${className}`} />
      <span className="font-medium">{label}</span>
    </span>
  );
}

function GridLines({ opacity }: { opacity: number }) {
  const lines = Array.from({ length: 9 });
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ opacity }}>
      {lines.map((_, idx) => (
        <div
          key={`h-${idx}`}
          className="absolute left-0 right-0 border-t border-white"
          style={{ top: `${(idx + 1) * 10}%` }}
        />
      ))}
      {lines.map((_, idx) => (
        <div
          key={`v-${idx}`}
          className="absolute top-0 bottom-0 border-l border-white"
          style={{ left: `${(idx + 1) * 10}%` }}
        />
      ))}
    </div>
  );
}

function MapMarker({
  location,
  statusClass,
  scan,
  countToday,
  recentCount,
  isActive,
  isEditMode,
  periodLabel,
  onDragStart,
  onDelete,
  onSelect,
  t,
  lang
}: {
  location: KioskLocationMeta;
  statusClass: string;
  scan: RecentScan | null;
  countToday: number;
  recentCount: number;
  isActive: boolean;
  isEditMode: boolean;
  periodLabel: string;
  onDragStart: (e: React.MouseEvent | React.PointerEvent) => void;
  onDelete: () => void;
  onSelect: () => void;
  t: (key: string, fallback?: string) => string;
  lang: LangCode;
}) {
  return (
    <div
      className={`group absolute flex flex-col items-center ${isEditMode ? 'cursor-grab active:cursor-grabbing hover:scale-110' : 'cursor-pointer'}`}
      style={{
        left: `${location.mapPosition.x}%`,
        top: `${location.mapPosition.y}%`,
        transform: "translate(-50%, -50%)",
        zIndex: isEditMode ? 100 : (isActive ? 50 : 10)
      }}
      onMouseDown={onDragStart}
      onPointerDown={onDragStart}
      onClick={(e) => {
        // In edit/placement modes the click is handled by drag/delete or the
        // map-level placement handler; only drill into history otherwise.
        if (isEditMode) return;
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Tooltip - Hide while editing to avoid visual clutter */}
      {!isEditMode && (
        <div className="pointer-events-none absolute bottom-full mb-3 flex flex-col items-center opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-[-4px]">
          <div className="rounded-xl bg-slate-900 text-white px-4 py-3 shadow-xl border border-white/10 min-w-[180px]">
            <div className="flex items-center justify-between mb-2 border-b border-white/10 pb-2">
              <span className="font-bold text-sm tracking-tight">{location.name}</span>
              <span className={`h-2 w-2 rounded-full ${statusClass} shadow-[0_0_8px_rgba(0,0,0,0.5)]`} />
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">{t("home.tooltipPosition", "Position")}</span>
                <span className="font-medium">{location.building} • {t("home.floorAbbr", "Fl {n}").replace("{n}", String(location.floor))}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">{periodLabel}</span>
                <span className="font-bold text-emerald-400">{countToday}</span>
              </div>
              {recentCount > 0 && (
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400">{t("home.lastHour", "Last Hour")}</span>
                  <span className="font-bold text-blue-400">+{recentCount}</span>
                </div>
              )}
              {scan && (
                <div className="flex justify-between text-[11px] mt-1 pt-1 border-t border-white/5">
                  <span className="text-slate-400">{t("home.lastScan", "Last Scan")}</span>
                  <span className="text-slate-200">{new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(new Date(scan.timestamp))}</span>
                </div>
              )}
            </div>
          </div>
          <div className="w-3 h-3 bg-slate-900 rotate-45 mt-[-6px] border-r border-b border-white/10" />
        </div>
      )}

      {/* Marker Point */}
      <div className="relative flex items-center justify-center">
        {/* Pulse Animation for Active Locations */}
        {isActive && (
          <>
            <div className={`absolute h-8 w-8 rounded-full animate-ping opacity-20 ${statusClass}`} />
            <div className={`absolute h-6 w-6 rounded-full animate-pulse opacity-40 ${statusClass}`} />
          </>
        )}

        {/* Main Dot */}
        <div className={`relative h-4 w-4 rounded-full border-2 border-white shadow-lg transition-transform duration-200 group-hover:scale-125 ${statusClass}`}>
          {/* Activity Badge (Small) */}
          {recentCount > 0 && (
            <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-[8px] font-bold px-1 rounded-md border border-white min-w-[14px] h-[14px] flex items-center justify-center">
              {recentCount}
            </div>
          )}
        </div>

        {/* Delete button (Edit Mode) */}
        {isEditMode && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute -top-2 -right-2 bg-white text-rose-500 rounded-full shadow-md hover:text-rose-700 hover:scale-110 transition-all z-[110]"
            title={t("home.deleteMarker", "Delete Marker")}
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Label (Visible on hover or if active) */}
      <span className={`mt-2 text-[10px] font-bold px-2 py-0.5 rounded-md bg-black/50 text-white backdrop-blur-sm border border-white/10 transition-opacity duration-200 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        {location.name}
      </span>
    </div>
  );
}
